import {useEffect, useMemo} from 'react';

import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react';
import {useQueries} from '@tanstack/react-query';

import {useShopperInventoryIds} from '@amblique/king-living/app/hooks/use-shopper-inventory-ids';

import {
  chunk,
  GET_OPTION_SLOT_PRODUCTS_PAGE_SIZE,
  GET_OPTION_SLOT_PRODUCTS_PARAMS,
  GET_PRODUCTS_STALE_TIME_MS,
} from '@amblique/king-living/app/utils/api-utils';
import {resolveRequiredSingleVisibleProductId} from '@amblique/king-living/app/utils/customize-selections';

/**
 * Shared data hook for option slot product fetching and auto-selection.
 *
 * - Derives product IDs from value.products when the slot is visible (`value.isVisible`).
 * - Fetches products in small SCAPI chunks (allImages off — 10MB cap) via React Query.
 * - Streams each chunk into `products` as it completes (catalog order among loaded SKUs).
 * - Auto-selects when slot is required with exactly one visible recommendation.
 *
 * @param {Object} value - Slot value from container.values (SFRA optionsGroupingUIProcessed format)
 * @param {Object|null} currentSelection - Current selection object for this slot
 * @param {Function} onAutoSelect - Called with the matched preselected product when auto-select fires
 * @param {Object} [options]
 * @param {boolean} [options.disablePreselect=false] - When true, suppresses the BM-driven
 *   auto-select. Used by Simple PDP option containers to honour FSD §4.1 — "no option will be
 *   pre-selected by default" — without affecting Complex PDP flyout behavior.
 * @returns {{ products: Array, isLoading: boolean, isLoadingMore: boolean }}
 */
export function useProductSlotData(value, currentSelection, onAutoSelect, {disablePreselect = false} = {}) {
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();
  const shopperInventoryIds = useShopperInventoryIds();

  const visibleProductIds = useMemo(() => {
    if (value?.isVisible === false) return [];

    return (value?.products || []).map((p) => p?.productId).filter(Boolean);
  }, [value?.isVisible, value?.products]);

  const invKey = shopperInventoryIds ?? '';

  const idChunks = useMemo(
    () => chunk(visibleProductIds, GET_OPTION_SLOT_PRODUCTS_PAGE_SIZE),
    [visibleProductIds]
  );

  const chunkQueries = useQueries({
    queries: idChunks.map((ids) => ({
      queryKey: ['king-living/getProducts', 'option-slot', ids.join(','), invKey],
      queryFn: async () => {
        const token = await getTokenWhenReady();
        const res = await api.shopperProducts.getProducts({
          parameters: {
            ids: ids.join(','),
            ...GET_OPTION_SLOT_PRODUCTS_PARAMS,
            ...(shopperInventoryIds ? {inventoryIds: shopperInventoryIds} : {}),
          },
          headers: {Authorization: `Bearer ${token}`},
        });

        return res?.data ?? [];
      },
      enabled: ids.length > 0,
      staleTime: GET_PRODUCTS_STALE_TIME_MS,
    })),
  });

  const products = useMemo(() => {
    const byId = new Map();

    chunkQueries.forEach(({data}) => {
      (data ?? []).forEach((product) => {
        if (product?.id) {
          byId.set(product.id, product);
        }
      });
    });

    return visibleProductIds.map((id) => byId.get(id)).filter(Boolean);
  }, [chunkQueries, visibleProductIds]);

  const isChunkInFlight = chunkQueries.some(({isLoading, isFetching}) => isLoading || isFetching);
  const isLoading = products.length === 0 && isChunkInFlight;
  const isLoadingMore = products.length > 0 && isChunkInFlight;

  // Auto-select required single-option slot after fetch, if no selection exists.
  // Intentional deps: only re-run when products array identity changes (on initial load).
  useEffect(() => {
    if (disablePreselect) return;
    if (!products.length || currentSelection || !value?.products || !onAutoSelect) return;

    const productId = resolveRequiredSingleVisibleProductId(value);

    if (!productId) return;

    const preselectedProduct = products.find((p) => p.id === productId);

    if (!preselectedProduct) return;

    onAutoSelect(preselectedProduct);
  }, [products, disablePreselect]); // Intentional: one-time init when products load

  return {products, isLoading, isLoadingMore};
}
